import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import {
  Card, CardContent, Typography, TextField, Button, Grid, Box,
  IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Snackbar, Alert, InputAdornment, CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LockIcon from '@mui/icons-material/Lock';
import SearchIcon from '@mui/icons-material/Search';
import api from '../../services/api';
import { maskCPF2 } from '../../utils/mask';

const EXPENSE_SUGGESTIONS = [
  'Combustível',
  'Transporte Diversos',
  'Despesas Administrativas',
  'Alimentação',
  'Hospedagens'
];

const parseBRL = (value) => {
  if (!value) return 0;
  const cleaned = String(value).replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const parsed = parseFloat(cleaned);
  return isNaN(parsed) ? 0 : parsed;
};

const maskCurrency = (value) => {
  const digits = String(value).replace(/\D/g, '');
  const number = parseInt(digits || '0', 10) / 100;
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatBRL = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const number = Number(value);
  if (isNaN(number)) return '';
  return number.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const emptyExpense = () => ({
  id: `${Date.now()}-${Math.random()}`,
  description: '',
  amount: '',
  receipt: null
});

export default function PrestacaoContasPublico() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [cpfLoading, setCpfLoading] = useState(false);
  const [cpfError, setCpfError] = useState('');
  const [cpfValidated, setCpfValidated] = useState(false);
  // status do link: 'aberto' (rascunho, editável), 'bloqueado' (já enviado/outro status) ou 'invalido' (token não encontrado)
  const [linkStatus, setLinkStatus] = useState('aberto');
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    description: '',
    proponent_name: '',
    proponent_role: '',
    proponent_document: '',
    expenses: [emptyExpense()]
  });

  const loadProposal = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/reimbursement-proposals/public/${encodeURIComponent(token)}`);
      const proposal = data?.data;
      console.log('Proposal data:', proposal);
      if (!proposal) {
        setLinkStatus('invalido');
        return;
      }
      const attr = proposal.attributes || proposal;
      if (attr.status !== 'Rascunho') {
        setLinkStatus('bloqueado');
        return;
      }
      setLinkStatus('aberto');
      setForm({
        description: attr.description || '',
        proponent_name: attr.proponent_name || '',
        proponent_role: attr.proponent_role || '',
        proponent_document: attr.proponent_document || '',
        expenses: (attr.expenses || []).length
          ? attr.expenses.map((exp, idx) => ({
              id: exp.id || `${Date.now()}-${idx}`,
              description: exp.description || '',
              amount: exp.amount !== null && exp.amount !== undefined ? formatBRL(exp.amount) : '',
              receipt: exp.receipt?.data || exp.receipt || null
            }))
          : [emptyExpense()]
      });
    } catch (err) {
      setLinkStatus('invalido');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProposal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleAddExpense = (description = '') => {
    setForm(prev => {
      const hasOnlyEmptyExpense = prev.expenses.length === 1
        && !prev.expenses[0].description
        && !prev.expenses[0].amount
        && !prev.expenses[0].receipt;

      if (description && hasOnlyEmptyExpense) {
        return {
          ...prev,
          expenses: [{ ...prev.expenses[0], description }]
        };
      }

      return { ...prev, expenses: [...prev.expenses, { ...emptyExpense(), description }] };
    });
  };

  const handleCpfLookup = async () => {
    if (!form.proponent_document || form.proponent_document.replace(/\D/g, '').length !== 11) {
      setCpfError('Digite um CPF válido para buscar');
      return;
    }

    setCpfLoading(true);
    setCpfError('');
    try {
      const { data } = await api.post('/subscriber/form', {
        data: { cpf: form.proponent_document }
      });

      if (!data) {
        setCpfError('Nenhum inscrito encontrado para este CPF');
        setCpfValidated(false);
        return;
      }

      setForm(prev => ({
        ...prev,
        proponent_name: data.name || '',
        proponent_role: data.office || data.role || ''
      }));
      setCpfValidated(true);
    } catch (err) {
      setCpfValidated(false);
      setCpfError(err.response?.data?.error?.message || 'Não foi possível localizar este CPF');
    } finally {
      setCpfLoading(false);
    }
  };

  const handleRemoveExpense = (expenseId) => {
    setForm(prev => ({ ...prev, expenses: prev.expenses.filter(e => e.id !== expenseId) }));
  };

  const handleExpenseChange = (expenseId, field, value) => {
    setForm(prev => ({
      ...prev,
      expenses: prev.expenses.map(e => {
        if (e.id !== expenseId) return e;
        if (field === 'amount') return { ...e, [field]: maskCurrency(value) };
        return { ...e, [field]: value };
      })
    }));
  };

  const handleFileChange = async (expenseId, file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('files', file);
    // Enquanto o link estiver em Rascunho, o comprovante é armazenado publicamente
    formData.append('private', 'false');
    formData.append('folderPath', 'public/uploads');

    try {
      const { data } = await api.post(`/reimbursement-proposals/public/${encodeURIComponent(token)}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploaded = Array.isArray(data) ? data[0] : data;
      handleExpenseChange(expenseId, 'receipt', uploaded);
    } catch (err) {
      setError('Erro ao enviar comprovante: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const totalAmount = useMemo(() => {
    return form.expenses.reduce((sum, exp) => sum + parseBRL(exp.amount), 0);
  }, [form.expenses]);

  const handleSubmit = async () => {
    if (!cpfValidated || !form.proponent_name || !form.proponent_role || !form.proponent_document) {
      setError('Informe o CPF e faça a busca antes de enviar a prestação de contas');
      return;
    }
    if (form.expenses.length === 0 || form.expenses.some(e => !e.description || !e.amount || !e.receipt)) {
      setError('Preencha descrição, valor e anexe o comprovante de todas as despesas');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        description: form.description,
        proponent_name: form.proponent_name,
        proponent_role: form.proponent_role,
        proponent_document: form.proponent_document,
        status: 'Pendente',
        expenses: form.expenses.map(exp => ({
          description: exp.description,
          amount: parseBRL(exp.amount),
          receipt: exp.receipt?.id || null
        }))
      };
      await api.put(`/reimbursement-proposals/public/${encodeURIComponent(token)}`, { data: payload });
      setSubmitted(true);
    } catch (err) {
      setError('Erro ao enviar prestação de contas: ' + (err.response?.data?.error?.message || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (linkStatus === 'invalido') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Card sx={{ maxWidth: 480 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <LockIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6">Link inválido</Typography>
            <Typography color="text.secondary">
              Este link de prestação de contas não existe ou foi removido.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  if (linkStatus === 'bloqueado' || submitted) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', p: 2 }}>
        <Card sx={{ maxWidth: 480 }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <CheckCircleIcon color="success" sx={{ fontSize: 48, mb: 1 }} />
            <Typography variant="h6">
              {submitted ? 'Prestação de contas enviada!' : 'Este link não está mais disponível'}
            </Typography>
            <Typography color="text.secondary">
              {submitted
                ? 'Sua prestação de contas foi enviada para análise. Você já pode fechar esta página.'
                : 'A prestação de contas já foi enviada e agora está em análise. O acesso público a este link foi bloqueado.'}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 }, maxWidth: 900, mx: 'auto' }}>
      <Card>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>Prestação de Contas</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <TextField
                  fullWidth
                  label="CPF do Proponente"
                  value={form.proponent_document}
                  onChange={(e) => {
                    setCpfValidated(false);
                    setCpfError('');
                    setForm(prev => ({ ...prev, proponent_document: maskCPF2(e.target.value), proponent_name: '', proponent_role: '' }));
                  }}
                  error={Boolean(cpfError)}
                  helperText={cpfError || 'Informe seu CPF e faça a busca para carregar seus dados'}
                  inputProps={{ maxLength: 14 }}
                  disabled={cpfLoading}
                />
                <Button
                  variant="contained"
                  onClick={handleCpfLookup}
                  disabled={cpfLoading || form.proponent_document.replace(/\D/g, '').length !== 11}
                  startIcon={cpfLoading ? <CircularProgress size={18} /> : <SearchIcon />}
                  sx={{ minWidth: 120, height: 56 }}
                >
                  Buscar
                </Button>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome do Proponente"
                value={form.proponent_name}
                InputProps={{ readOnly: true }}
                disabled={!cpfValidated}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Cargo"
                value={form.proponent_role}
                InputProps={{ readOnly: true }}
                disabled={!cpfValidated}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Descrição Livre"
                value={form.description}
                onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
              />
            </Grid>
          </Grid>

          <Typography variant="subtitle1" sx={{ mt: 3, mb: 1 }}>Despesas</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            {EXPENSE_SUGGESTIONS.map((suggestion, index) => (
              <Button
                key={suggestion}
                size="small"
                variant="outlined"
                onClick={() => handleAddExpense(suggestion)}
              >
                {index === 0 ? suggestion : `${index} ${suggestion}`}
              </Button>
            ))}
          </Box>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Descrição</TableCell>
                  <TableCell width="180px">Valor (R$)</TableCell>
                  <TableCell width="220px">Comprovante</TableCell>
                  <TableCell width="80px" align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {form.expenses.map((exp) => (
                  <TableRow key={exp.id}>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={exp.description}
                        onChange={(e) => handleExpenseChange(exp.id, 'description', e.target.value)}
                        placeholder="Descrição da despesa"
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        size="small"
                        value={exp.amount}
                        onChange={(e) => handleExpenseChange(exp.id, 'amount', e.target.value)}
                        placeholder="0,00"
                        InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Button variant="outlined" component="label" size="small" startIcon={<AttachFileIcon />}>
                          {exp.receipt ? 'Trocar' : 'Anexar'}
                          <input
                            type="file"
                            hidden
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange(exp.id, e.target.files[0])}
                          />
                        </Button>
                        {exp.receipt && (
                          <Typography variant="caption" noWrap sx={{ maxWidth: 100 }}>
                            {exp.receipt.name || 'Comprovante'}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <IconButton color="error" onClick={() => handleRemoveExpense(exp.id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Button variant="text" startIcon={<AddIcon />} onClick={handleAddExpense} sx={{ mt: 1 }}>
            Adicionar Despesa
          </Button>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
            <Typography variant="h6">Total: {formatBRL(totalAmount)}</Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<SendIcon />}
              disabled={submitting}
              onClick={handleSubmit}
            >
              {submitting ? 'Enviando...' : 'Enviar Prestação de Contas'}
            </Button>
          </Box>
        </CardContent>
      </Card>

      <Snackbar open={!!error} autoHideDuration={4000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
        <Alert severity="error" onClose={() => setError('')}>{error}</Alert>
      </Snackbar>
    </Box>
  );
}
